/**
 * Normalizzazione degli errori restituiti dal proxy eBay del backend.
 *
 * Il backend (features/ebay/controller.v2.js) inoltra la risposta di eBay
 * nella forma { service, method, error: { errors: [...] }, debug }.
 * I formatter storici leggevano solo `data.error`, che e' un OGGETTO e non
 * l'array: finivano quindi sempre in JSON.stringify, rendendo il messaggio
 * illeggibile per il cliente.
 */

const STAGE_LABELS = {
	createOrReplaceInventoryItem: 'Creazione scheda prodotto',
	createOffer: 'Creazione offerta',
	updateOffer: 'Aggiornamento offerta',
	publishOffer: 'Pubblicazione offerta',
};

const extractEbayErrors = error => {
	const data = error?.response?.data;

	if (!data) {
		return [];
	}

	if (Array.isArray(data?.error?.errors)) {
		return data.error.errors;
	}

	if (Array.isArray(data?.errors)) {
		return data.errors;
	}

	if (Array.isArray(data?.error)) {
		return data.error;
	}

	return [];
};

const formatEbayErrorParameters = parameters => {
	if (!Array.isArray(parameters) || parameters.length === 0) {
		return '';
	}

	const formatted = parameters
		.filter(parameter => parameter?.value)
		.map(parameter => (parameter?.name ? `${parameter.name}: ${parameter.value}` : parameter.value));

	return formatted.length > 0 ? ` (${formatted.join(', ')})` : '';
};

/**
 * Trasforma un singolo errore eBay in una riga leggibile, senza mai scartare
 * il messaggio: "[25002] Offer entity already exists. (offerId: 1134928875016)"
 */
const formatEbayErrorEntry = entry => {
	// i nostri controller rispondono { error: ['testo', ...] }
	if (typeof entry === 'string') {
		return entry.trim();
	}
	const text = entry?.longMessage || entry?.message || '';
	const id = entry?.errorId ? `[${entry.errorId}] ` : '';
	const parameters = formatEbayErrorParameters(entry?.parameters);

	if (!text && !parameters) {
		return '';
	}

	return `${id}${text}${parameters}`.trim();
};

/**
 * Messaggio unico e leggibile da mostrare al cliente.
 */
const formatEbayApiError = (error, fallback = 'Operazione non riuscita') => {
	const entries = extractEbayErrors(error).map(formatEbayErrorEntry).filter(Boolean);

	if (entries.length > 0) {
		return entries.join('<br>');
	}

	const data = error?.response?.data;
	const message = data?.message || data?.error?.message;

	if (typeof message === 'string' && message) {
		return message;
	}

	if (data?.error && typeof data.error === 'object') {
		return JSON.stringify(data.error);
	}

	// Nessuna risposta HTTP: richiesta mai arrivata o risposta bloccata (rete, CORS, payload).
	if (!error?.response && error?.message) {
		const status = error?.request?.status;
		const detail = status ? `${error.message} (status ${status})` : error.message;
		return `Nessuna risposta dal server: ${detail}`;
	}

	if (error?.message) {
		return error.message;
	}

	// Risposta senza corpo interpretabile (es. pagina di errore del proxy): almeno lo status.
	if (error?.response?.status) {
		return `${fallback} (HTTP ${error.response.status})`;
	}

	return fallback;
};

/**
 * Campi fissi della pagina di pubblicazione eBay riconoscibili dal testo dell'errore.
 * L'ordine conta: vince la prima regola che corrisponde.
 */
const EBAY_FIELD_KEYWORDS = [
	{ key: 'categoryId', re: /\bcategor(y|ia)\b|categoryId/i },
	{ key: 'shippingPolicy', re: /fulfillment\s*polic|shipping\s*(polic|service)|fulfillmentPolicyId/i },
	{ key: 'paymentPolicy', re: /payment\s*polic|paymentPolicyId/i },
	{ key: 'returnPolicy', re: /return\s*polic|returnPolicyId/i },
	{ key: 'inventoryLocation', re: /merchantLocationKey|inventory\s*location|item\s*location/i },
	{ key: 'ean', re: /\b(ean|gtin|upc|isbn)\b|product\s*identifier/i },
	{ key: 'pictures', re: /\b(picture|image|photo)s?\b|imageUrls/i },
	{ key: 'condition', re: /\bcondition\b/i },
	{ key: 'title', re: /\btitle\b/i },
	{ key: 'description', re: /\bdescription\b/i },
	{ key: 'price', re: /\bprice\b|pricingSummary/i },
	{ key: 'quantity', re: /\bquantity\b/i },
	{ key: 'postalCode', re: /postal\s*code|\bzip\b/i },
	{ key: 'country', re: /\bcountry\b/i },
];

const escapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ebayErrorText = entry => {
	if (typeof entry === 'string') {
		return entry;
	}
	const parameters = (entry?.parameters || []).map(parameter => parameter?.value).filter(Boolean);
	return [entry?.longMessage, entry?.message, ...parameters].filter(Boolean).join(' ');
};

/**
 * Collega gli errori eBay ai campi della pagina: [{ key, message }].
 * key = 'aspect:<nome aspetto>' oppure una chiave di EBAY_FIELD_KEYWORDS.
 * Gli errori che non si riescono a collegare non compaiono qui (restano nel dialog).
 */
const mapEbayErrorsToFields = (errors, aspectNames = []) => {
	const result = [];
	// prima i nomi lunghi: "Tipo di prodotto" non deve finire su "Tipo"
	const names = [...aspectNames].filter(Boolean).sort((a, b) => b.length - a.length);

	for (const entry of errors || []) {
		const text = ebayErrorText(entry);
		if (!text) {
			continue;
		}
		const message = formatEbayErrorEntry(entry);

		// "The item specific Marca is missing..."
		const specific = text.match(/item specifics?\s+["']?(.+?)["']?\s+(is|are|has|have|must|should|cannot|can't|value)\b/i);
		let aspect = specific ? names.find(name => name.toLowerCase() === specific[1].trim().toLowerCase()) : null;
		if (!aspect) {
			aspect = names.find(name => new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(name)}($|[^\\p{L}\\p{N}])`, 'iu').test(text));
		}
		if (aspect) {
			result.push({ key: `aspect:${aspect}`, message });
			continue;
		}

		const field = EBAY_FIELD_KEYWORDS.find(rule => rule.re.test(text));
		if (field) {
			result.push({ key: field.key, message });
		}
	}

	return result;
};

const getEbayStageLabel = stage => STAGE_LABELS[stage] || stage || '';

export { extractEbayErrors, formatEbayApiError, formatEbayErrorEntry, getEbayStageLabel, mapEbayErrorsToFields };
